import { wishlist } from '@otto-ec/nexus-registry/wishlist';
import { createEventMergeContext, submitEventMerge } from '@otto-ec/tracking-bct';
import { AdjustTracking } from './AdjustTracking';
import { FirebaseTracking } from './FirebaseTracking';
import { triggerHapticFeedback } from '@otto-ec/apps-api/hapticfeedback';
import { submitToExactag } from '@otto-ec/online-marketing/exactag';

export class NexusApi {
    constructor(private readonly adjustTracking: AdjustTracking, private readonly firebaseTracking: FirebaseTracking) {}

    init() {
        wishlist.assignNamespace({
            addItem: this.addItem.bind(this),
            deleteItem: this.deleteItem.bind(this),
            setAvailabilityAlertStatus: this.setAvailabilityAlertStatus.bind(this)
        });
    }

    async addItem(variationId: string, qualified: boolean, client: string, wishlistId?: string) {
        if (typeof variationId === 'undefined') {
            throw new Error("Required field 'variationId' missing. No Add2WL will be sent!");
        }
        if (typeof qualified === 'undefined') {
            throw new Error("Required field 'qualified' missing. No Add2WL will be sent!");
        }

        const operation = 'ADD';
        const context = await createEventMergeContext();
        const eventMergeId = context.eventMergeId;
        const data = {
            items: [
                {
                    variationId,
                    qualified
                }
            ],
            client,
            sourceUrl: window.location.pathname + window.location.search,
            wishlistId,
            operation,
            eventMergeId
        };

        const payload = {
            features: [],
            name: 'add'
        };

        submitEventMerge(eventMergeId, {}, payload);
        return this.makeRequest(data, true, variationId, eventMergeId);
    }

    async deleteItem(variationId: string, client: string, wishlistId?: string, deletionInteractionType?: string) {
        const sourceUrl = window.location.pathname + window.location.search;
        const context = await createEventMergeContext();
        const eventMergeId = context.eventMergeId;
        const items = [{ variationId }];
        const operation = 'REMOVE';
        const data = {
            client,
            items,
            sourceUrl,
            eventMergeId,
            wishlistId,
            operation,
            deletionInteractionType
        };

        const payload = {
            features: [],
            name: 'delete'
        };

        submitEventMerge(eventMergeId, {}, payload);
        return this.makeRequest(data, false, variationId, eventMergeId);
    }

    async setAvailabilityAlertStatus(variationId: string, enabled: boolean, client: string, wishlistId?: string) {
        const context = await createEventMergeContext();
        const eventMergeId = context.eventMergeId;
        const data = {
            variationId,
            enabled,
            client,
            wishlistId,
            eventMergeId
        };

        /*            */

        const headers = { 'Content-Type': 'application/json' };
        const body = JSON.stringify(data);
        const response = await fetch('/wishlist-view/items/availabilityAlert', { headers, body, method: 'POST' });
        if (response.status === 204) {
            return { success: true, eventMergeId };
        } else {
            return { success: false, eventMergeId };
        }
    }

    private async makeRequest(data: unknown, isAddOperation: boolean, variationId: string, eventMergeId: string) {
        const headers = { 'Content-Type': 'application/json' };
        const body = JSON.stringify(data);
        const response = await fetch('/wishlist-view/items', { headers, body, method: 'POST' });
        if (response.status === 200) {
            const responseJson = await response.json();
            if (responseJson.success) {
                this.signalWishlistChanged(responseJson.newAmount);
                this.trackExactag(isAddOperation, variationId);
                this.trackAdjust(isAddOperation, responseJson.articleNumber, responseJson.variationLink);
                this.trackFirebase(isAddOperation, responseJson.articleNumber);
                this.triggerHapticFeedback(isAddOperation);
                return { success: responseJson.success, eventMergeId };
            } else {
                this.signalWishlistChangedEmpty();
                return { success: false, eventMergeId };
            }
        } else {
            this.signalWishlistChangedEmpty();
            return { success: false, eventMergeId };
        }
    }

    private signalWishlistChanged(newAmount: number) {
        document.dispatchEvent(new CustomEvent('wishlist.changed', { detail: { newAmount } }));
    }

    /**
 *
 *
 */
    private signalWishlistChangedEmpty() {
        document.dispatchEvent(new CustomEvent('wishlist.changed'));
    }

    private trackExactag(isAddOperation: boolean, variationId: string) {
        if (isAddOperation) {
            submitToExactag({
                pt: 'add_to_wishlist',
                mv: variationId
            });
        }
    }

    private trackAdjust(isAddOperation: boolean, articleNumber: string, variationLink: string) {
        if (isAddOperation) {
            this.adjustTracking.trackAdjust(articleNumber, variationLink);
        }
    }

    private trackFirebase(isAddOperation: boolean, articleNumber: string) {
        if (isAddOperation) {
            this.firebaseTracking.trackFirebase(articleNumber);
        }
    }

    private triggerHapticFeedback(isAddOperation: boolean) {
        if (isAddOperation) {
            triggerHapticFeedback();
        }
    }
}
